import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Mail, Check, X as XIcon, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { formatINR } from '../utils/formatCurrency';
import { extractApiError } from '../utils/apiError';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// Don't stack on top of LoginDialogs' backup-tip — Radix Dialogs both render
// at z-50, and stacked overlays intercept clicks on whichever modal is below.
const BACKUP_TIP_KEY = 'equitrack:backup_tip_dismissed_session';
const isBackupTipPending = () => {
  try { return sessionStorage.getItem(BACKUP_TIP_KEY) !== '1'; }
  catch (_) { return false; }
};

/**
 * On-login review dialog for email-imported transactions.
 *
 * Rationale: broker contract notes occasionally use a company name that
 * doesn't match the exchange ticker (e.g. "HINDUSTAN CONSTRUCTI" while
 * MC has "HCC"). When such a parse happens via the email pipeline there's
 * no human-in-the-loop step, so the holding ends up with a symbol that has
 * no live price. This dialog surfaces those imported transactions after
 * login and lets the user (a) accept, (b) edit the symbol (which also
 * persists a symbol_mapping for future emails), or (c) reject (delete).
 *
 * Shown once per session until 'needs_review' rows count is zero.
 */
export const EmailImportReviewDialog = ({ onResolved }) => {
  const [groups, setGroups] = useState(null); // null = not yet loaded
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  // Per-row edited symbol (in-memory; flushed on Accept).
  const [editSym, setEditSym] = useState({});
  const [saving, setSaving] = useState(null); // txn id being saved

  // Fetch exactly once per mount; dialog stays open until the user clears
  // every pending row or dismisses.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/email-imported/pending`, {
          withCredentials: true,
        });
        if (cancelled) return;
        setGroups(data?.groups || []);
        setTotal(data?.total || 0);
        // Seed editable-symbol state with current values.
        const seed = {};
        (data?.groups || []).forEach((g) => {
          (g.rows || []).forEach((r) => { seed[r.id] = r.symbol; });
        });
        setEditSym(seed);
        if ((data?.total || 0) > 0) {
          // Defer until the once-per-session backup-tip dialog is dismissed
          // so we don't stack two overlays at z-50 (which intercepts clicks).
          // Capped retry — LoginDialogs now writes the session flag on all
          // paths, but if it fails we open anyway after ~15s.
          let _tipRetries = 0;
          const openWhenSafe = () => {
            if (cancelled) return;
            if (_tipRetries < 25 && isBackupTipPending()) {
              _tipRetries += 1;
              setTimeout(openWhenSafe, 600);
            } else {
              setOpen(true);
            }
          };
          openWhenSafe();
        }
      } catch (e) {
        // 402/403/etc. — just skip silently, this is a background check.
        // eslint-disable-next-line no-console
        console.debug('pending email imports fetch failed', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const flatRows = useMemo(() => {
    if (!groups) return [];
    return groups.flatMap((g) => g.rows.map((r) => ({ ...r, portfolio_name: g.portfolio_name })));
  }, [groups]);

  const refreshAfterMutation = async () => {
    const { data } = await axios.get(`${API_URL}/api/email-imported/pending`, { withCredentials: true });
    setGroups(data?.groups || []);
    setTotal(data?.total || 0);
    if ((data?.total || 0) === 0) {
      setOpen(false);
      if (onResolved) onResolved();
    }
  };

  const handleAccept = async (row) => {
    setSaving(row.id);
    try {
      const newSym = (editSym[row.id] || '').trim().toUpperCase();
      await axios.post(
        `${API_URL}/api/email-imported/${row.id}/confirm`,
        newSym && newSym !== (row.symbol || '').toUpperCase() ? { symbol: newSym } : {},
        { withCredentials: true },
      );
      toast.success(newSym && newSym !== row.symbol
        ? `Updated ${row.symbol} → ${newSym} (future imports will auto-map)`
        : `Confirmed ${row.symbol}`);
      await refreshAfterMutation();
    } catch (e) {
      toast.error(extractApiError(e, 'Failed to confirm'));
    } finally {
      setSaving(null);
    }
  };

  const handleReject = async (row) => {
    if (!window.confirm(`Delete this ${row.symbol} ${row.transaction_type} transaction?`)) return;
    setSaving(row.id);
    try {
      await axios.post(`${API_URL}/api/email-imported/${row.id}/reject`, {}, { withCredentials: true });
      toast.success('Removed');
      await refreshAfterMutation();
    } catch (e) {
      toast.error(extractApiError(e, 'Failed to delete'));
    } finally {
      setSaving(null);
    }
  };

  const handleAcceptAll = async () => {
    setSaving('all');
    try {
      const { data } = await axios.post(
        `${API_URL}/api/email-imported/confirm-all`, {},
        { withCredentials: true },
      );
      toast.success(`Confirmed ${data.updated || 0} transaction${data.updated === 1 ? '' : 's'}`);
      setOpen(false);
      setTotal(0);
      if (onResolved) onResolved();
    } catch (e) {
      toast.error(extractApiError(e, 'Failed to confirm all'));
    } finally {
      setSaving(null);
    }
  };

  // F&O accept/reject — separate endpoints since trades live in
  // fno_transactions collection.
  const handleFnoAccept = async (row) => {
    setSaving(row.id);
    try {
      await axios.post(`${API_URL}/api/email-imported/fno/${row.id}/confirm`, {}, { withCredentials: true });
      toast.success(`Confirmed F&O trade`);
      await refreshAfterMutation();
    } catch (e) {
      toast.error(extractApiError(e, 'Failed to confirm'));
    } finally { setSaving(null); }
  };
  const handleFnoReject = async (row) => {
    const label = row.instrument_type === 'OPT'
      ? `${row.underlying} ${row.strike} ${row.option_type}`
      : `${row.underlying} FUT`;
    if (!window.confirm(`Delete this ${row.side?.toUpperCase()} ${label} trade?`)) return;
    setSaving(row.id);
    try {
      await axios.post(`${API_URL}/api/email-imported/fno/${row.id}/reject`, {}, { withCredentials: true });
      toast.success('Removed');
      await refreshAfterMutation();
    } catch (e) {
      toast.error(extractApiError(e, 'Failed to delete'));
    } finally { setSaving(null); }
  };
  const fmtFnoLabel = (r) => r.instrument_type === 'OPT'
    ? `${r.underlying} ${(r.expiry || '').slice(0, 10)} ${r.strike} ${r.option_type}`
    : `${r.underlying} ${(r.expiry || '').slice(0, 10)} FUT`;

  if (!groups || total === 0) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-w-[860px] w-[95vw] max-h-[85vh] overflow-y-auto"
        style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E5DF' }}
        data-testid="email-import-review-dialog"
      >
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div
              className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{ backgroundColor: '#EBF3F0' }}
            >
              <Mail className="h-4 w-4" style={{ color: '#1B4332', strokeWidth: 1.7 }} />
            </div>
            <div>
              <DialogTitle style={{ color: '#1C1917', fontFamily: 'Work Sans' }}>
                Review {total} auto-imported transaction{total === 1 ? '' : 's'}
              </DialogTitle>
              <p className="text-xs mt-1" style={{ color: '#57534E', lineHeight: 1.5 }}>
                These were pulled from emails you forwarded. If the symbol doesn&apos;t match the exchange ticker, edit it below — we&apos;ll remember the mapping for next time.
              </p>
            </div>
          </div>
        </DialogHeader>

        {groups.map((g) => (
          <div key={g.portfolio_id} className="mt-4 first:mt-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#7A7A70' }}>
              {g.portfolio_name}
            </p>

            {/* Equity transactions — mobile cards + desktop table */}
            {g.rows && g.rows.length > 0 && (
              <>
                <p className="text-[10px] font-medium uppercase tracking-wider mb-1.5" style={{ color: '#1B4332' }}>
                  Equity ({g.rows.length})
                </p>

                {/* MOBILE: stacked cards (no horizontal scroll) */}
                <div className="md:hidden space-y-2 mb-3">
                  {g.rows.map((r) => {
                    const priceMismatch = !r.current_price || r.current_price <= 0;
                    const nameMismatch = priceMismatch || r.parsed_needs_mapping;
                    return (
                      <div key={r.id} className="rounded-lg p-3" style={{ backgroundColor: '#FAFAF7', border: '1px solid #F0F0EB' }} data-testid={`email-import-review-mobile-card-${r.id}`}>
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium uppercase" style={{
                            backgroundColor: r.transaction_type === 'buy' ? '#EBF3F0' : '#FAF5ED',
                            color: r.transaction_type === 'buy' ? '#1B4332' : '#7A5C2E',
                          }}>{r.transaction_type}</span>
                          <span className="text-[11px]" style={{ color: '#57534E' }}>
                            {r.transaction_date ? new Date(r.transaction_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-') : '—'}
                          </span>
                        </div>
                        <div className="mb-2">
                          <p className="text-[10px] uppercase tracking-wider mb-0.5" style={{ color: '#78716C' }}>Symbol (edit if wrong)</p>
                          <Input
                            value={editSym[r.id] || ''}
                            onChange={(e) => setEditSym((s) => ({ ...s, [r.id]: e.target.value.toUpperCase() }))}
                            className="h-8 text-sm"
                            data-testid={`email-import-symbol-input-${r.id}`}
                            style={{ borderColor: nameMismatch ? '#E8A98B' : '#E5E5DF' }}
                          />
                        </div>
                        <p className="text-[11px] mb-1.5" style={{ color: '#7A7A70' }}>
                          From: <span title={r.raw_name}>{r.raw_name}</span>
                        </p>
                        <div className="flex items-center justify-between text-xs tabular-nums mb-2">
                          <span style={{ color: '#57534E' }}>{r.quantity} × {formatINR(r.price, { decimals: 2 })}</span>
                          {priceMismatch ? (
                            <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: '#9A3412' }}>
                              <AlertTriangle className="h-3 w-3" /> No live match
                            </span>
                          ) : (
                            <span style={{ color: '#2D6A4F' }}>Live {formatINR(r.current_price, { decimals: 2 })}</span>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => handleAccept(r)} disabled={saving === r.id || saving === 'all'}
                                  data-testid={`email-import-accept-${r.id}`}
                                  className="flex-1 h-8 text-xs rounded inline-flex items-center justify-center gap-1"
                                  style={{ backgroundColor: '#1B4332', color: '#FFFFFF' }}>
                            {saving === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                            Accept
                          </button>
                          <button onClick={() => handleReject(r)} disabled={saving === r.id || saving === 'all'}
                                  data-testid={`email-import-reject-${r.id}`}
                                  className="flex-1 h-8 text-xs rounded inline-flex items-center justify-center gap-1"
                                  style={{ backgroundColor: '#FFFFFF', color: '#9A3412', border: '1px solid #E8A98B' }}>
                            <XIcon className="h-3 w-3" />
                            Reject
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* DESKTOP: original table */}
                <div className="hidden md:block border rounded-md overflow-hidden mb-3" style={{ borderColor: '#E5E5DF' }}>
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ backgroundColor: '#FAFAF7' }}>
                    <th className="text-left px-3 py-2 font-medium" style={{ color: '#57534E' }}>Date</th>
                    <th className="text-left px-3 py-2 font-medium" style={{ color: '#57534E' }}>Side</th>
                    <th className="text-left px-3 py-2 font-medium" style={{ color: '#57534E' }}>Symbol (edit if wrong)</th>
                    <th className="text-left px-3 py-2 font-medium" style={{ color: '#57534E' }}>Parsed From</th>
                    <th className="text-right px-3 py-2 font-medium" style={{ color: '#57534E' }}>Qty × Price</th>
                    <th className="text-right px-3 py-2 font-medium" style={{ color: '#57534E' }}>Live Px</th>
                    <th className="text-right px-3 py-2 font-medium" style={{ color: '#57534E' }}>Action</th>
                  </tr>
                </thead>
                <tbody className="zebra-tbody">
                  {g.rows.map((r) => {
                    const priceMismatch = !r.current_price || r.current_price <= 0;
                    const nameMismatch = priceMismatch || r.parsed_needs_mapping;
                    return (
                      <tr key={r.id} data-testid={`email-import-review-row-${r.id}`} style={{ borderTop: '1px solid #F0F0EB' }}>
                        <td className="px-3 py-2 whitespace-nowrap" style={{ color: '#1C1917' }}>
                          {r.transaction_date ? new Date(r.transaction_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-') : '—'}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className="px-1.5 py-0.5 rounded text-[10px] font-medium uppercase"
                            style={{
                              backgroundColor: r.transaction_type === 'buy' ? '#EBF3F0' : '#FAF5ED',
                              color: r.transaction_type === 'buy' ? '#1B4332' : '#7A5C2E',
                            }}
                          >
                            {r.transaction_type}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            value={editSym[r.id] || ''}
                            onChange={(e) => setEditSym((s) => ({ ...s, [r.id]: e.target.value.toUpperCase() }))}
                            className="h-7 text-xs"
                            data-testid={`email-import-symbol-input-${r.id}`}
                            style={{ minWidth: 100, borderColor: nameMismatch ? '#E8A98B' : '#E5E5DF' }}
                          />
                        </td>
                        <td className="px-3 py-2 truncate max-w-[180px]" style={{ color: '#7A7A70' }} title={r.raw_name}>
                          {r.raw_name}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums" style={{ color: '#1C1917' }}>
                          {r.quantity} × {formatINR(r.price, { decimals: 2 })}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {priceMismatch ? (
                            <span className="inline-flex items-center gap-1" style={{ color: '#9A3412' }}>
                              <AlertTriangle className="h-3 w-3" />
                              No match
                            </span>
                          ) : (
                            <span style={{ color: '#2D6A4F' }}>{formatINR(r.current_price, { decimals: 2 })}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => handleAccept(r)}
                              disabled={saving === r.id || saving === 'all'}
                              data-testid={`email-import-accept-${r.id}`}
                              className="h-7 w-7 inline-flex items-center justify-center rounded transition-colors hover:bg-[#EBF3F0]"
                              title={(editSym[r.id] || '').toUpperCase() !== (r.symbol || '').toUpperCase()
                                ? `Update symbol to ${editSym[r.id]} & accept`
                                : 'Accept'}
                            >
                              {saving === r.id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: '#1B4332' }} />
                              ) : (
                                <Check className="h-3.5 w-3.5" style={{ color: '#1B4332', strokeWidth: 2 }} />
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleReject(r)}
                              disabled={saving === r.id || saving === 'all'}
                              data-testid={`email-import-reject-${r.id}`}
                              className="h-7 w-7 inline-flex items-center justify-center rounded transition-colors hover:bg-[#FCECE8]"
                              title="Reject & delete this transaction"
                            >
                              <XIcon className="h-3.5 w-3.5" style={{ color: '#9A3412', strokeWidth: 2 }} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
              </>
            )}

            {/* F&O segment — same dialog, separate collection */}
            {g.fno_rows && g.fno_rows.length > 0 && (
              <>
                <p className="text-[10px] font-medium uppercase tracking-wider mb-1.5 mt-2" style={{ color: '#1B4332' }}>
                  Futures &amp; Options ({g.fno_rows.length})
                </p>
                {/* MOBILE: stacked F&O cards */}
                <div className="md:hidden space-y-2 mb-3">
                  {g.fno_rows.map((r) => (
                    <div key={r.id} className="rounded-lg p-3" style={{ backgroundColor: '#F5F8F4', border: '1px solid #D6E5DC' }} data-testid={`email-fno-review-mobile-card-${r.id}`}>
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <p className="text-sm font-medium" style={{ color: '#1B4332' }}>{fmtFnoLabel(r)}</p>
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium uppercase" style={{
                          backgroundColor: r.side === 'buy' ? '#EBF3F0' : '#FAF5ED',
                          color: r.side === 'buy' ? '#1B4332' : '#7A5C2E',
                        }}>{r.side}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[11px] tabular-nums" style={{ color: '#57534E' }}>
                        <span>Qty {r.quantity}</span>
                        <span className="text-right">Px {formatINR(r.price, { decimals: 2 })}</span>
                        <span>Brokerage {formatINR(r.brokerage, { decimals: 2 })}</span>
                        <span className="text-right">STT {formatINR(r.stt_ctt, { decimals: 2 })}</span>
                      </div>
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => handleFnoAccept(r)} disabled={saving === r.id || saving === 'all'}
                                data-testid={`email-fno-accept-${r.id}`}
                                className="flex-1 h-8 text-xs rounded inline-flex items-center justify-center gap-1"
                                style={{ backgroundColor: '#1B4332', color: '#FFFFFF' }}>
                          {saving === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                          Accept
                        </button>
                        <button onClick={() => handleFnoReject(r)} disabled={saving === r.id || saving === 'all'}
                                data-testid={`email-fno-reject-${r.id}`}
                                className="flex-1 h-8 text-xs rounded inline-flex items-center justify-center gap-1"
                                style={{ backgroundColor: '#FFFFFF', color: '#9A3412', border: '1px solid #E8A98B' }}>
                          <XIcon className="h-3 w-3" /> Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                {/* DESKTOP: F&O table */}
                <div className="hidden md:block border rounded-md overflow-hidden" style={{ borderColor: '#D6E5DC' }}>
                  <table className="w-full text-xs">
                    <thead>
                      <tr style={{ backgroundColor: '#F5F8F4' }}>
                        <th className="text-left px-3 py-2 font-medium" style={{ color: '#1B4332' }}>Date</th>
                        <th className="text-left px-3 py-2 font-medium" style={{ color: '#1B4332' }}>Side</th>
                        <th className="text-left px-3 py-2 font-medium" style={{ color: '#1B4332' }}>Contract</th>
                        <th className="text-right px-3 py-2 font-medium" style={{ color: '#1B4332' }}>Qty × Price</th>
                        <th className="text-right px-3 py-2 font-medium" style={{ color: '#1B4332' }}>Charges</th>
                        <th className="text-right px-3 py-2 font-medium" style={{ color: '#1B4332' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.fno_rows.map((r) => (
                        <tr key={r.id} data-testid={`email-fno-review-row-${r.id}`} style={{ borderTop: '1px solid #E0EBE3' }}>
                          <td className="px-3 py-2" style={{ color: '#1C1917' }}>{(r.transaction_date || '').slice(0, 10)}</td>
                          <td className="px-3 py-2">
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium uppercase" style={{
                              backgroundColor: r.side === 'buy' ? '#EBF3F0' : '#FAF5ED',
                              color: r.side === 'buy' ? '#1B4332' : '#7A5C2E',
                            }}>{r.side}</span>
                          </td>
                          <td className="px-3 py-2 font-medium" style={{ color: '#1B4332' }}>{fmtFnoLabel(r)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{r.quantity} × {formatINR(r.price, { decimals: 2 })}</td>
                          <td className="px-3 py-2 text-right tabular-nums" style={{ color: '#57534E' }}>{formatINR((r.brokerage || 0) + (r.stt_ctt || 0), { decimals: 2 })}</td>
                          <td className="px-3 py-2 text-right">
                            <div className="inline-flex items-center gap-1">
                              <button onClick={() => handleFnoAccept(r)} disabled={saving === r.id}
                                      data-testid={`email-fno-accept-${r.id}`}
                                      className="h-7 w-7 inline-flex items-center justify-center rounded transition-colors hover:bg-[#EBF3F0]">
                                {saving === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: '#1B4332' }} /> : <Check className="h-3.5 w-3.5" style={{ color: '#1B4332', strokeWidth: 2 }} />}
                              </button>
                              <button onClick={() => handleFnoReject(r)} disabled={saving === r.id}
                                      data-testid={`email-fno-reject-${r.id}`}
                                      className="h-7 w-7 inline-flex items-center justify-center rounded transition-colors hover:bg-[#FCECE8]">
                                <XIcon className="h-3.5 w-3.5" style={{ color: '#9A3412', strokeWidth: 2 }} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        ))}

        <div className="flex items-center justify-end gap-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            data-testid="email-import-review-remind-later"
          >
            Remind me later
          </Button>
          <Button
            type="button"
            onClick={handleAcceptAll}
            disabled={saving === 'all' || flatRows.length === 0}
            data-testid="email-import-review-accept-all"
            style={{ backgroundColor: '#1B4332', color: '#FFFFFF' }}
          >
            {saving === 'all' ? 'Accepting…' : `Accept all ${total}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
